import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

type Action = 'create' | 'reset' | 'destroy' | 'add_employees' | 'remove_employees' | 'status';

export default function DemoOrgPanel({ onChanged }: { onChanged: () => void }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState<Action | null>(null);
  const [count, setCount] = useState(5);
  const [lastResult, setLastResult] = useState<string | null>(null);

  async function run(action: Action, extra: Record<string, unknown> = {}) {
    setBusy(action);
    setLastResult(null);
    const { data, error } = await supabase.functions.invoke('seed-demo-org', {
      body: { action, ...extra },
    });
    setBusy(null);
    if (error) {
      toast({ title: `Demo ${action} failed`, description: error.message, variant: 'destructive' });
      return;
    }
    setLastResult(JSON.stringify(data, null, 2));
    toast({ title: `Demo ${action} complete`, description: 'See details below.' });
    onChanged();
  }

  return (
    <div className="border border-border rounded-lg bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-lg font-semibold">Northwind Demo</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Seed a fully-populated demo tenant (30 employees, 60 days of pulses, 6-anchor survey,
        ~120 conversations, sample insights). Add or remove synthetic employees on demand.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => run('create')} disabled={busy !== null}>
          {busy === 'create' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Create
        </Button>
        <Button size="sm" variant="outline" onClick={() => run('reset')} disabled={busy !== null}>
          {busy === 'reset' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Reset
        </Button>
        <Button size="sm" variant="outline" onClick={() => run('status')} disabled={busy !== null}>
          {busy === 'status' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Status
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          onClick={() => run('destroy')}
          disabled={busy !== null}
        >
          {busy === 'destroy' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Destroy
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Label htmlFor="demo-count" className="text-xs text-muted-foreground">Count</Label>
          <Input
            id="demo-count"
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            className="w-20 h-8"
          />
          <Button size="sm" variant="outline" onClick={() => run('add_employees', { count })} disabled={busy !== null}>
            {busy === 'add_employees' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add employees
          </Button>
          <Button size="sm" variant="outline" onClick={() => run('remove_employees', { count })} disabled={busy !== null}>
            {busy === 'remove_employees' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Remove employees
          </Button>
        </div>
      </div>
      {lastResult && (
        <pre className="text-xs bg-muted rounded p-2 overflow-auto max-h-48">{lastResult}</pre>
      )}
    </div>
  );
}
